import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

const PX_PERCENT_UNIT_CHOICES = [
  { value: "px", label: "px" },
  { value: "%", label: "%" },
] as const;

const PX_REM_PERCENT_UNIT_CHOICES = [
  { value: "px", label: "px" },
  { value: "rem", label: "rem" },
  { value: "%", label: "%" },
] as const;

const PERCENT_UNIT_CHOICES = [{ value: "%", label: "%" }] as const;

const lengthUnitValue = (
  labelKey: string,
  helpKey: string,
  value: { unit: string; value: number },
  options?: {
    min?: number;
    max?: number;
    step?: number;
    maxFractionDigits?: number;
    choices?: readonly { value: string; label: string }[];
  },
) =>
  propertyValue({
    type: "unit_value",
    value,
    options: {
      label: labelKey,
      help: helpKey,
      min: options?.min ?? 0,
      ...(options?.max !== undefined ? { max: options.max } : {}),
      ...(options?.step !== undefined ? { step: options.step } : {}),
      ...(options?.maxFractionDigits !== undefined
        ? { maxFractionDigits: options.maxFractionDigits }
        : {}),
      choices: [...(options?.choices ?? PX_PERCENT_UNIT_CHOICES)],
    },
  });

const percentUnitValue = (
  labelKey: string,
  helpKey: string,
  value: { unit: "%"; value: number },
  options?: { min?: number; max?: number; step?: number },
) =>
  propertyValue({
    type: "unit_value",
    value,
    options: {
      label: labelKey,
      help: helpKey,
      min: options?.min ?? 0,
      ...(options?.max !== undefined ? { max: options.max } : {}),
      ...(options?.step !== undefined ? { step: options.step } : {}),
      choices: [...PERCENT_UNIT_CHOICES],
    },
  });

/** Serializable preview product used in kiosk shop category list scenarios. */
export type KioskShopCategoryListPreviewProduct = {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
};

/** Serializable preview category used in kiosk shop category list scenarios. */
export type KioskShopCategoryListPreviewCategory = {
  id: string;
  name: string;
  icon?: string;
  level?: number;
  hasDescendantWithVisibleProducts?: boolean;
  productsInformation: readonly KioskShopCategoryListPreviewProduct[];
};

/**
 * Serializable preview content for `KioskShopCategoryListBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type KioskShopCategoryListBlockScenarioContent = {
  shop: {
    id: string;
    name: string;
    available: boolean;
    available_at?: string;
  } | null;
  isShopLoading: boolean;
  categoriesWithProducts: readonly KioskShopCategoryListPreviewCategory[];
  warningMessages: readonly string[];
  /** Preview-only: category id shown as the open/selected card. */
  selectedCategoryId?: string | null;
};
export const KIOSKSHOPCATEGORYLISTBLOCK_LEGACY_FIELD_ORDER = [
  {
    type: "heading",
    value: "pages.blocks.kiosk-shop-category-list.fieldset.categories.label",
  },
  "display_category_name",
  "category_card_padding",
  "category_image_ratio",
  "category_card_gap",
  "category_card_border_radius",
  "category_cards_position",
  "item_background_color",
  "unselected_category_border_color",
  "item_selected_background_color",
  "item_text_color",
  "item_selected_text_color",
  {
    type: "heading",
    value: "pages.blocks.kiosk-shop-category-list.fieldset.border.label",
  },
  "display_selected_category_border",
  "selected_category_border_color",
  "selected_category_border_width",
  {
    type: "heading",
    value: "pages.blocks.kiosk-shop-category-list.fieldset.halo.label",
  },
  "display_selected_category_halo",
  "selected_category_halo_color",
  "selected_category_halo_opacity",
  "selected_category_shadow_spread",
  {
    type: "heading",
    value: "pages.blocks.kiosk-shop-category-list.fieldset.zoom.label",
  },
  "display_selected_category_zoom",
  "selected_category_zoom_scale",
  {
    type: "heading",
    value: "pages.blocks.kiosk-shop-category-list.fieldset.arrows.label",
  },
  "display_arrows",
  "display_dots",
  "background_color",
  "accent_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskShopCategoryListBlockDescriptor: _generatedKioskShopCategoryListBlockDescriptor,
  kioskShopCategoryListBlockBlocDefinition,
  kioskShopCategoryListBlockDashboardLayout,
  kioskShopCategoryListBlockConfig,
  kioskShopCategoryListBlockResolveConfig,
  kioskShopCategoryListBlockProperties,
  kioskShopCategoryListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Kiosk Shop - Category list",
    componentType: "KioskShopCategoryListBlockComponent",
    category: "kiosk",
    description: "Kiosk Shop - Category list.",
    keywords: [],
    allowedIn: [
      { bloc: 'KioskShopPageComponent', slot: 'sidebar' },
    ],


    sharedFields: {},
    /**
     * New blocs start rounded. Saved blocs without this key keep the 0px
     * property default so existing kiosks do not change shape.
     */
    creationDefaults: {
      category_card_border_radius: { unit: "rem", value: 0.5 },
    },
  },
  () => ({
    display_category_name: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.display-category-name.label",
        help: "pages.blocks.kiosk-shop-category-list.display-category-name.help",
      },
    }),
    category_image_ratio: propertyValue({
      type: "text",
      value: "2/1",
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.category-image-ratio.label",
        help: "pages.blocks.kiosk-shop-category-list.category-image-ratio.help",
      },
    }),
    category_card_padding: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.category-card-padding.label",
        help: "pages.blocks.kiosk-shop-category-list.category-card-padding.help",
      },
    }),
    category_card_gap: lengthUnitValue(
      "pages.blocks.kiosk-shop-category-list.category-card-gap.label",
      "pages.blocks.kiosk-shop-category-list.category-card-gap.help",
      { unit: "px", value: 10 },
    ),
    category_card_border_radius: lengthUnitValue(
      "pages.blocks.kiosk-shop-category-list.category-card-border-radius.label",
      "pages.blocks.kiosk-shop-category-list.category-card-border-radius.help",
      { unit: "px", value: 0 },
      { step: 0.01, maxFractionDigits: 2, choices: PX_REM_PERCENT_UNIT_CHOICES },
    ),
    category_cards_position: propertyValue({
      type: "select",
      value: "top",
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.category-cards-position.label",
        help: "pages.blocks.kiosk-shop-category-list.category-cards-position.help",
        choices: [
          {
            value: "top",
            label:
              "pages.blocks.kiosk-shop-category-list.category-cards-position.options.top",
          },
          {
            value: "center",
            label:
              "pages.blocks.kiosk-shop-category-list.category-cards-position.options.center",
          },
          {
            value: "bottom",
            label:
              "pages.blocks.kiosk-shop-category-list.category-cards-position.options.bottom",
          },
        ],
      },
    }),
    display_arrows: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.kiosk-shop-category-list.display-arrows.label",
      },
    }),
    display_dots: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.kiosk-shop-category-list.display-dots.label",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-500" },
      options: {
        label: "pages.blocks.kiosk-shop-category-list.background-color.label",
        help: "pages.blocks.kiosk-shop-category-list.background-color.help",
      },
    }),
    item_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-300" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.item-background-color.label",
      },
    }),
    item_selected_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-400" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.item-selected-background-color.label",
      },
    }),
    display_selected_category_border: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-border.label",
        help:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-border.help",
      },
    }),
    selected_category_border_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-600" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.selected-category-border-color.label",
      },
    }),
    unselected_category_border_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.unselected-category-border-color.label",
      },
    }),
    selected_category_border_width: lengthUnitValue(
      "pages.blocks.kiosk-shop-category-list.selected-category-border-width.label",
      "pages.blocks.kiosk-shop-category-list.selected-category-border-width.help",
      { unit: "px", value: 1 },
    ),
    display_selected_category_halo: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-halo.label",
        help:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-halo.help",
      },
    }),
    selected_category_halo_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-600" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.selected-category-halo-color.label",
      },
    }),
    selected_category_halo_opacity: propertyValue({
      type: "slider",
      value: 40,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.selected-category-halo-opacity.label",
        help:
          "pages.blocks.kiosk-shop-category-list.selected-category-halo-opacity.help",
        min: 0,
        max: 100,
        step: 1,
      },
    }),
    selected_category_shadow_spread: lengthUnitValue(
      "pages.blocks.kiosk-shop-category-list.selected-category-shadow-spread.label",
      "pages.blocks.kiosk-shop-category-list.selected-category-shadow-spread.help",
      { unit: "px", value: 8 },
    ),
    display_selected_category_zoom: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-zoom.label",
        help:
          "pages.blocks.kiosk-shop-category-list.display-selected-category-zoom.help",
      },
    }),
    selected_category_zoom_scale: percentUnitValue(
      "pages.blocks.kiosk-shop-category-list.selected-category-zoom-scale.label",
      "pages.blocks.kiosk-shop-category-list.selected-category-zoom-scale.help",
      { unit: "%", value: 108 },
      { min: 100, max: 130, step: 1 },
    ),
    item_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.kiosk-shop-category-list.item-text-color.label",
      },
    }),
    item_selected_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label:
          "pages.blocks.kiosk-shop-category-list.item-selected-text-color.label",
      },
    }),
    accent_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-300" },
      options: {
        label: "pages.blocks.kiosk-shop-category-list.accent-color.label",
        help: "pages.blocks.kiosk-shop-category-list.accent-color.help",
      },
    }),
  }),
  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-shop-category-list.tab.content.label",
        fieldsets: [
          {
            legend:
              "pages.blocks.kiosk-shop-category-list.fieldset.categories.label",
            fields: [
              "display_category_name",
              "category_card_padding",
              "category_image_ratio",
              "category_card_gap",
              "category_card_border_radius",
              "category_cards_position",
              "item_background_color",
              {
                prop: "unselected_category_border_color",
                visibleWhen: { display_selected_category_border: true },
              },
              "item_selected_background_color",
              {
                prop: "item_text_color",
                visibleWhen: { display_category_name: true },
              },
              {
                prop: "item_selected_text_color",
                visibleWhen: { display_category_name: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-shop-category-list.fieldset.border.label",
            fields: [
              "display_selected_category_border",
              {
                prop: "selected_category_border_color",
                visibleWhen: { display_selected_category_border: true },
              },
              {
                prop: "selected_category_border_width",
                visibleWhen: { display_selected_category_border: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-shop-category-list.fieldset.halo.label",
            fields: [
              "display_selected_category_halo",
              {
                prop: "selected_category_halo_color",
                visibleWhen: { display_selected_category_halo: true },
              },
              {
                prop: "selected_category_halo_opacity",
                visibleWhen: { display_selected_category_halo: true },
              },
              {
                prop: "selected_category_shadow_spread",
                visibleWhen: { display_selected_category_halo: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-shop-category-list.fieldset.zoom.label",
            fields: [
              "display_selected_category_zoom",
              {
                prop: "selected_category_zoom_scale",
                visibleWhen: { display_selected_category_zoom: true },
              },
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-shop-category-list.fieldset.arrows.label",
            fields: [
              "display_arrows",
              "display_dots",
              "background_color",
              "accent_color",
            ],
          },
        ],
      },
    ],
  }),
);

export function kioskShopCategoryListBlockDescriptor(): IBlockDescriptor;
export function kioskShopCategoryListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskShopCategoryListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskShopCategoryListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskShopCategoryListBlockDescriptor,
    kioskShopCategoryListBlockProperties,
    fieldOrder,
  );
}

export {
  kioskShopCategoryListBlockBlocDefinition,
  kioskShopCategoryListBlockDashboardLayout,
  kioskShopCategoryListBlockProperties,
  kioskShopCategoryListBlockConfig,
  kioskShopCategoryListBlockResolveConfig,
  kioskShopCategoryListBlockDefinitionSetupScenarios,
};

export type KioskShopCategoryListBlockConfig =
  typeof kioskShopCategoryListBlockConfig;
